import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DomainStateService {
  private readonly _prefix$ = new BehaviorSubject<string>('');
  private readonly _isSuperAdmin$ = new BehaviorSubject<boolean>(false);
  private readonly _isAtLeastAdmin$ = new BehaviorSubject<boolean>(false);
  private readonly _domainsChanged$ = new Subject<void>();
  readonly domainsChanged$ = this._domainsChanged$.asObservable();
  private _lastLoadedEmail: string | null = null;
  private readonly _domainNames = new Map<string, string>();

  // Ruolo reale dell'utente autenticato (non cambia durante l'impersonation)
  private _realIsSuperAdmin = false;
  private _realIsAtLeastAdmin = false;
  private _isImpersonating = false;

  readonly selectedDomainPrefix$ = this._prefix$.asObservable();
  readonly isSuperAdmin$ = this._isSuperAdmin$.asObservable();

  get selectedDomainPrefix(): string { return this._prefix$.value; }
  get isSuperAdmin(): boolean { return this._isSuperAdmin$.value; }
  get isAtLeastAdmin(): boolean { return this._isAtLeastAdmin$.value; }
  get realIsSuperAdmin(): boolean { return this._realIsSuperAdmin; }
  get lastLoadedEmail(): string | null { return this._lastLoadedEmail; }

  setLastLoadedEmail(email: string): void { this._lastLoadedEmail = email; }

  getDomainName(prefix: string): string | undefined { return this._domainNames.get(prefix); }
  setDomainNames(domains: { prefix: string; name: string }[]): void {
    domains.forEach(d => this._domainNames.set(d.prefix, d.name));
  }

  private _storageKey = 'radoff_selected_domain_prefix';
  static readonly ALL_CUSTOMERS_SENTINEL = '__ALL__';

  setUserContext(email: string): void {
    this._storageKey = `radoff_domain_${email}`;
  }

  setDomain(prefix: string): void {
    if (this._prefix$.value === prefix) return;
    this._prefix$.next(prefix);
    // Store sentinel '__ALL__' for empty prefix so a refresh restores "All customers" mode.
    // resetUser() bypasses this method and calls removeItem() directly, preserving logout behaviour.
    localStorage.setItem(this._storageKey, prefix || DomainStateService.ALL_CUSTOMERS_SENTINEL);
  }

  getStoredDomain(): string | null {
    return localStorage.getItem(this._storageKey);
  }

  /** Aggiorna il ruolo reale. Non sovrascrive il ruolo effettivo se in impersonation. */
  setIsSuperAdmin(value: boolean): void {
    this._realIsSuperAdmin = value;
    if (!this._isImpersonating) {
      this._isSuperAdmin$.next(value);
    }
  }

  /** Imposta il flag admin-o-superiore (rilevato dalla prima pagina dell'API /domains). */
  setIsAtLeastAdmin(value: boolean): void {
    this._realIsAtLeastAdmin = value;
    if (!this._isImpersonating) {
      this._isAtLeastAdmin$.next(value);
    }
  }

  /** Avvia impersonation: il super admin vede la piattaforma con il ruolo del cliente selezionato. */
  startImpersonation(isAtLeastAdmin: boolean): void {
    this._isImpersonating = true;
    this._isSuperAdmin$.next(false);
    this._isAtLeastAdmin$.next(isAtLeastAdmin);
  }

  /** Termina impersonation: ripristina il ruolo reale del super admin. */
  stopImpersonation(): void {
    this._isImpersonating = false;
    this._isSuperAdmin$.next(this._realIsSuperAdmin);
    this._isAtLeastAdmin$.next(this._realIsAtLeastAdmin);
  }

  notifyDomainsChanged(): void {
    this._domainsChanged$.next();
  }

  resetUser(): void {
    localStorage.removeItem(this._storageKey);
    this._storageKey = 'radoff_selected_domain_prefix';
    this._lastLoadedEmail = null;
    this._realIsSuperAdmin = false;
    this._realIsAtLeastAdmin = false;
    this._isImpersonating = false;
    this._isSuperAdmin$.next(false);
    this._isAtLeastAdmin$.next(false);
    this._prefix$.next('');
    this._domainNames.clear();
  }
}
